/* eslint-disable no-use-before-define */
import React from 'react'
import Autocomplete from '@mui/lab/Autocomplete'
import TextField from '@mui/material/TextField'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { withStyles } from '~/themes/useStyles'
import Tag from './Tag'
import { COLORS } from '~/themes/common'

const style = () => ({
  inputRoot: {
    alignItems: 'flex-start',
  },
  disabledInput: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: COLORS.gray.disableGray2,
    },
  },
  tag: {
    marginRight: 5,
    marginTop: 5,
    boxShadow: '0 0 0 1px #C7C7C7',
    maxWidth: 'calc(100% - 5px)',
    '&.Mui-disabled': {
      opacity: 1,
      boxShadow: 'unset',
    },
  },
})

const TextInputStyled = withStyles(() => ({
  root: {
    '& .MuiFormHelperText-contained': {
      marginRight: 3,
      marginLeft: 3,
    },

    '& .MuiFormHelperText-root.Mui-error': {
      color: COLORS.accent.rubyRed1,
    },

    '&.leftItem': {
      '& .MuiFormHelperText-contained': {
        marginRight: 3,
      },
    },
  },
}))(TextField)

const InputTag = ({
  classes,
  tags,
  onTagsChange,
  readOnly,
  disabledInput,
  unique,
  disabledTag,
  helperText,
  error,
  renderTags,
  ignoreSensitive,
  placeholder,
  disabled,
  ...props
}) => {
  const [inputTag, setInputTag] = React.useState('')
  const onInputTagChange = (_, value) => {
    if (value === ',') return
    setInputTag(value)
  }

  const handleKeyDown = (event) => {
    if (!inputTag) return
    switch (event.key) {
      case 'Enter':
      case ',':
      case 'Tab':
        if (unique) {
          if (ignoreSensitive && (tags || []).indexOf(inputTag.trim()) > -1) {
            return
          }
          if (
            !ignoreSensitive &&
            (tags || []).findIndex(
              (t) => t.toLowerCase() === inputTag.toLowerCase().trim(),
            ) > -1
          ) {
            return
          }
        }
        setInputTag('')
        const newTags = [...tags, inputTag]
        onTagsChange(newTags, inputTag)
        event.preventDefault()
        break
      default:
        break
    }
  }

  const handleTags = (value, getTagProps) => {
    if (renderTags) {
      return renderTags(value, getTagProps, {
        onDelete: (tag) =>
          onTagsChange(
            value.filter((item) => item !== tag),
            tag,
          ),
      })
    }
    return value.map((tag, index) => (
      <Tag
        key={`tag${index}`}
        label={tag}
        classes={{
          root: classes.tag,
        }}
        onDelete={() => {
          onTagsChange(
            value.filter((item) => item !== tag),
            tag,
          )
        }}
        disabled={disabledTag || disabled}
      />
    ))
  }

  return (
    <Autocomplete
      {...props}
      classes={classes}
      multiple
      options={[]}
      value={tags}
      open={false}
      disabled={disabledInput || disabled}
      disableClearable
      forcePopupIcon={false}
      inputValue={inputTag}
      onInputChange={onInputTagChange}
      onKeyDown={handleKeyDown}
      renderTags={handleTags}
      renderInput={(params) => (
        <TextInputStyled
          {...params}
          className={clsx({
            [classes.disabledInput]: disabled,
          })}
          error={error}
          placeholder={placeholder}
          variant="outlined"
          inputProps={{
            ...params?.inputProps,
            readOnly,
          }}
          helperText={helperText}
        />
      )}
    />
  )
}
InputTag.defaultProps = {
  tags: [],
  onTagsChange: () => {},
  readOnly: false,
  disabledInput: false,
  unique: false,
  disabledTag: false,
  disabled: false,
  helperText: '',
  error: false,
  renderTags: null,
  ignoreSensitive: false,
  placeholder: '',
}

InputTag.propTypes = {
  classes: PropTypes.shape().isRequired,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  tags: PropTypes.oneOfType([PropTypes.shape(), PropTypes.array]),
  onTagsChange: PropTypes.func,
  readOnly: PropTypes.bool,
  disabledInput: PropTypes.bool,
  unique: PropTypes.bool,
  disabledTag: PropTypes.bool,
  disabled: PropTypes.bool,
  renderTags: PropTypes.func,
  ignoreSensitive: PropTypes.bool,
  placeholder: PropTypes.string,
}

export default withStyles(style)(InputTag)
